/* The kneading screen's buttons and panels (see kneading.tsx). They take the base look of
   a panel from the global `.panel`. */

.root {
  transition: opacity 0.3s;
}

:global(.at-title) .root,
:global(.shelf-open) .root {
  display: none;
}

:global(.delivering) .root {
  opacity: 0;
  pointer-events: none;
}

/* After submitting, the clay is locked until the next attempt; the result has its own
   buttons for what comes next. */
:global(.judged) .dock,
:global(.judged) .status,
:global(.judged) .undo {
  opacity: 0.4;
  pointer-events: none;
}

:global(.judged) .done {
  opacity: 0;
  pointer-events: none;
}

/* ---------- How it is going: the kitten says, with the strokes counted beside ---------- */

.status {
  top: 16px;
  left: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px 8px 8px;
}

.kitten {
  width: 48px;
  height: 48px;
  flex: none;
}

.mood {
  position: relative;
  margin: 0 0 0 4px;
  min-width: 10em;
  padding: 7px 12px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: #fff;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
}

/* The bubble's tail, toward the kitten. */
.mood::before {
  content: "";
  position: absolute;
  left: -6px;
  top: 50%;
  width: 10px;
  height: 10px;
  border-left: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  background: #fff;
  transform: translateY(-50%) rotate(45deg);
}

.strokes {
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 2px;
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-soft);
  white-space: nowrap;
}

.strokes b {
  font-size: 26px;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

/* A paw print by the count, pressed on again with each stroke. */
.paw {
  --size: 17px;
  display: inline-flex;
  /* Down to the foot of 手 (a little under the baseline): the print's bottom pad sits a
     sixth of it above its box's foot. */
  position: relative;
  top: calc(var(--size) / 6 + 1px);
  margin-left: 3px;
  color: var(--accent);
  transform: rotate(-14deg);
}

.paw svg {
  width: var(--size);
  height: var(--size);
}

/* ---------- What to make: the request over the target preview, at the top right ---------- */

.target {
  position: fixed;
  top: 16px;
  right: 16px;
  width: var(--preview-size);
}

.targetHead {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  width: 100%;
  height: var(--target-head);
  padding: 4px 26px 4px 12px;
  border-radius: 14px 14px 0 0;
  border-bottom: none;
  font: inherit;
  text-align: left;
  color: var(--ink);
  cursor: pointer;
}

/* "Choose another request" chevron. */
.targetHead::after {
  content: "›";
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-55%);
  font-size: 20px;
  color: var(--ink-soft);
}

:global(.judged) .targetHead {
  pointer-events: none;
}

:global(.judged) .targetHead::after {
  content: none;
}

.requestTitle,
.requestWish {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.requestTitle {
  font-size: 13px;
  font-weight: 700;
}

.requestWish {
  font-size: 11px;
  color: var(--accent);
}

/* The preview itself is drawn on the main canvas underneath this box. */
.targetView {
  width: var(--preview-size);
  height: var(--preview-size);
  border: 1px solid var(--line);
  border-top: none;
  pointer-events: none;
}

/* ---------- The dock: tools, modes and the brush size, at the bottom centre ---------- */

.dock {
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 10px 10px;
}

.brush {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 6px;
}

.brush input {
  flex: 1;
  accent-color: var(--accent);
}

.brushDot {
  flex: none;
  border-radius: 50%;
  background: var(--ink-soft);
  opacity: 0.6;
}

.small {
  width: 5px;
  height: 5px;
}

.big {
  width: 13px;
  height: 13px;
}

.row,
.tools {
  display: flex;
  gap: 5px;
}

.divider {
  width: 1px;
  margin: 6px 3px;
  background: var(--line);
}

.tool {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 58px;
  padding: 8px 8px 6px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
  font: inherit;
  font-size: 11px;
  font-weight: 700;
  color: var(--ink);
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, transform 0.1s;
}

.tool:hover {
  background: #fff8f1;
}

/* What the lesson points at: a frame closes in on it from far outside, and stays round it. */
.tutorialFocus {
  position: relative;
}

.tutorialFocus::before {
  content: "";
  position: absolute;
  inset: -5px;
  border: 3px solid var(--accent);
  border-radius: 15px;
  box-shadow: 0 0 12px rgba(217, 119, 79, 0.45);
  pointer-events: none;
  animation: focus-in 0.75s cubic-bezier(0.2, 0.8, 0.25, 1) both;
}

@keyframes focus-in {
  from {
    inset: -60vmax;
    border-radius: 60px;
    opacity: 0;
  }
  25% {
    opacity: 1;
  }
}

.round.tutorialFocus::before {
  border-radius: 50%;
}

.done.tutorialFocus::before {
  inset: -7px;
  border-radius: 99px;
}

.tutorialNudge::before {
  animation:
    focus-in 0.75s cubic-bezier(0.2, 0.8, 0.25, 1) both,
    bob 1.4s ease-in-out 0.75s infinite;
}

@keyframes bob {
  50% {
    transform: scale(1.06);
    box-shadow: 0 0 20px rgba(217, 119, 79, 0.7);
  }
}

/* Beside the brush slider, at the end it is to go to: chevrons lighting one after another. */
.brushWay {
  flex: none;
  display: flex;
  gap: 1px;
  pointer-events: none;
}

.brushWay.leftward {
  transform: scaleX(-1);
}

.brushWay i {
  width: 10px;
  height: 10px;
  border-top: 4px solid var(--accent);
  border-right: 4px solid var(--accent);
  border-radius: 2px;
  transform: rotate(45deg);
  opacity: 0.25;
  animation: chevron 0.9s ease-in-out infinite;
}

.brushWay i:nth-child(2) {
  animation-delay: 0.15s;
}

.brushWay i:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes chevron {
  30% {
    opacity: 1;
    transform: translateX(3px) rotate(45deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .tutorialFocus::before,
  .tutorialNudge::before,
  .brushWay i {
    animation: none;
  }

  .brushWay i {
    opacity: 1;
  }
}

.tool:active {
  transform: scale(0.94);
}

.tool[aria-checked="true"] {
  background: var(--accent-soft);
  border-color: var(--accent);
  color: #b85a2e;
}

.mode[aria-pressed="true"] {
  background: rgba(95, 183, 162, 0.16);
  border-color: var(--mint);
  color: #2f7d6b;
}

.key {
  position: absolute;
  top: 3px;
  left: 6px;
  font-family: inherit;
  font-size: 9px;
  color: var(--ink-soft);
}

/* What a stroke in the mode counts for. */
.cost {
  position: absolute;
  top: -8px;
  right: -6px;
  padding: 1px 6px;
  border-radius: 99px;
  background: #ffe2a8;
  color: #8a5a16;
  font-size: 10px;
}

/* A word on what a button does, above it, once the pointer rests there. */
.tool[data-tip]::after,
.round[data-tip]::after {
  content: attr(data-tip);
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  padding: 5px 9px;
  border-radius: 8px;
  background: var(--ink);
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transform: translateX(-50%) translateY(4px);
  transition: opacity 0.15s, transform 0.15s;
}

.round[data-tip]::after {
  left: 0;
  transform: translateY(4px);
}

.tool[data-tip]:hover::after,
.tool[data-tip]:focus-visible::after {
  opacity: 1;
  transform: translateX(-50%);
  transition-delay: 0.35s;
}

.round[data-tip]:hover::after,
.round[data-tip]:focus-visible::after {
  opacity: 1;
  transform: none;
  transition-delay: 0.35s;
}

/* ---------- Undo, start over and help: round buttons at the bottom left ---------- */

.corner {
  position: fixed;
  left: 16px;
  bottom: 16px;
  display: flex;
  gap: 8px;
}

.round {
  position: relative;
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  border: 1px solid var(--line);
  border-radius: 50%;
  background: var(--panel);
  backdrop-filter: blur(6px);
  box-shadow: 0 4px 14px rgba(120, 80, 50, 0.12);
  font: inherit;
  font-size: 20px;
  font-weight: 700;
  color: var(--ink);
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
}

.round:hover {
  background: #fff;
}

.round:active {
  transform: scale(0.92);
}

.round:disabled {
  opacity: 0.4;
  cursor: default;
}

/* ---------- Done: the one big button, at the bottom right ---------- */

.done {
  position: fixed;
  right: 16px;
  bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px 13px 22px;
  border: none;
  border-radius: 99px;
  background: var(--accent);
  color: #fff;
  font: inherit;
  font-size: 19px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 0 #c8693f, 0 10px 24px rgba(200, 105, 63, 0.3);
  transition: transform 0.12s, box-shadow 0.12s;
}

.done:hover {
  transform: translateY(-2px);
}

.done:active {
  transform: translateY(3px);
  box-shadow: 0 1px 0 #c8693f, 0 4px 10px rgba(200, 105, 63, 0.3);
}

.doneIcon {
  width: 24px;
  height: 24px;
}

/* ---------- How to knead: a card over the screen ---------- */

.help {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(90, 58, 46, 0.25);
  z-index: 13;
}

.card {
  position: relative;
  width: min(520px, 100%);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  padding: 18px 20px 20px;
  background: #fffaf3;
  animation: pop-in 0.3s cubic-bezier(0.3, 0.7, 0.4, 1.2);
}

@keyframes pop-in {
  0% {
    opacity: 0;
    transform: translateY(12px) scale(0.97);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

.title {
  margin: 0 0 4px;
  font-size: 20px;
}

.lead {
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--ink-soft);
}

.helpTools,
.helpNotes {
  margin: 0;
  padding: 0;
  list-style: none;
}

/* One grid for all the rows, so the keys and the words line up. */
.helpTools {
  display: grid;
  grid-template-columns: 22px auto auto 1fr;
  align-items: center;
  gap: 8px 10px;
  margin-bottom: 14px;
  font-size: 13px;
}

.helpTools li {
  display: contents;
}

.helpTools span {
  color: var(--ink-soft);
}

.helpKey {
  justify-self: start;
  padding: 1px 6px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: #fff;
  font-family: inherit;
  font-size: 11px;
  color: var(--ink-soft);
}

.helpNotes {
  display: grid;
  gap: 6px;
  margin-bottom: 16px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(122, 86, 68, 0.06);
  font-size: 12px;
  line-height: 1.5;
  color: var(--ink-soft);
}

.close {
  width: 100%;
  padding: 10px 0 9px;
}

.tutorialReplay {
  width: 100%;
  margin-bottom: 8px;
  padding: 9px 12px;
  border: 1px solid var(--accent);
  border-radius: 10px;
  background: #fff8f1;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

:global(.at-title) .tutorialReplay {
  display: none;
}

/* ---------- Small screens ---------- */

@media (max-width: 640px) {
  .key {
    display: none;
  }

  .target {
    top: 10px;
    right: 10px;
  }

  .targetHead {
    padding: 4px 18px 4px 8px;
  }

  .targetHead::after {
    right: 6px;
  }

  .requestTitle {
    font-size: 11px;
  }

  .requestWish {
    font-size: 10px;
  }

  /* As wide as the room beside the target. */
  .status {
    top: 10px;
    left: 10px;
    width: calc(100vw - 30px - var(--preview-size));
    gap: 6px;
    padding: 6px 10px 6px 6px;
  }

  .kitten {
    width: 36px;
    height: 36px;
  }

  .mood {
    flex: 1;
    min-width: 0;
    padding: 6px 9px;
    font-size: 13px;
  }

  .strokes b {
    font-size: 20px;
  }

  .paw {
    --size: 14px;
  }

  /* The dock spans the bottom, its buttons sharing the width; undo and done sit above it. */
  .dock {
    left: 10px;
    right: 10px;
    bottom: 10px;
    transform: none;
    padding: 6px 6px 8px;
  }

  .row,
  .tools {
    gap: 3px;
  }

  .tools {
    flex: 5;
  }

  .tool {
    flex: 1;
    min-width: 0;
    padding: 6px 1px 5px;
    font-size: 10px;
  }

  .divider {
    margin: 6px 1px;
  }

  .corner {
    left: 10px;
    bottom: 104px;
  }

  .round {
    width: 42px;
    height: 42px;
  }

  .done {
    right: 10px;
    bottom: 104px;
    padding: 11px 20px 10px 16px;
    font-size: 16px;
  }

  .helpTools {
    grid-template-columns: 22px auto 1fr;
  }

  .helpKey {
    display: none;
  }
}
